import * as React from "react";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteValue
} from "@mui/base/useAutocomplete/useAutocomplete";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";
import { FilterOptionsState } from "@mui/material/useAutocomplete";

import { get } from "lodash";

const EMPTY_ARRAY: any[] = [];

type DetailAutocompleteFormFieldProps<
  K,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = {
  name: string;
  label: string;
  autoFocus?: boolean;
  disableClearable?: DisableClearable;
  enablePortal?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiple?: Multiple;
  disableCloseOnSelect?: boolean;
  options?: K[];
  noOptionsText?: string;
  isLoading?: boolean;
  getOptionLabel: (
    option: K | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) => string;
  isOptionEqualToValue?: (option: K, value: K) => boolean;
  inputFormat?: string;
  popupIcon?: ReactNode;
  openOnFocus?: boolean;
  autoHighlight?: boolean;
  freeSolo?: FreeSolo;
  filterOptions?: (options: Array<K>, state: FilterOptionsState<K>) => Array<K>;
  onOptionSelected?: (
    option: AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>,
    field?: string
  ) => void;
  transformValue?: (
    value: AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>
  ) => AutocompleteValue<K, Multiple, DisableClearable, FreeSolo>;
  onTextInputChange?: (text: string) => void;
  blurOnSelect?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onSubmit?: () => void;
  sx?: SxProps;
  containerSx?: SxProps;
  groupBy?: (option: K) => string;
  inputSx?: SxProps;
  /**
   * Override the Paper that wraps the listbox. Use to render footers (e.g.
   * "type to search for more" hints) inside the popper without getting
   * occluded by the listbox itself.
   */
  PaperComponent?: React.ElementType;
};

export type RhfDetailAutocompleteFormFieldProps<
  K,
  T extends FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = DetailAutocompleteFormFieldProps<K, Multiple, DisableClearable, FreeSolo> &
  UseControllerProps<T> &
  Pick<
    AutocompleteProps<K, Multiple, DisableClearable, FreeSolo>,
    "renderOption" | "ListboxComponent" | "renderTags"
  >;

export default function AutocompleteFormField<
  K,
  T extends FieldValues,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  name,
  label,
  autoFocus,
  disableClearable,
  enablePortal = false,
  multiple,
  freeSolo,
  disabled = false,
  disableCloseOnSelect,
  control,
  required = false,
  options = [],
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  renderTags,
  isLoading = false,
  noOptionsText,
  popupIcon = <ExpandMoreOutlinedIcon />,
  filterOptions,
  onOptionSelected,
  transformValue,
  onTextInputChange,
  onSubmit,
  openOnFocus,
  autoHighlight,
  placeholder,
  groupBy,
  blurOnSelect = true,
  inputRef: inputRefProp,
  ListboxComponent,
  rules = {},
  sx = { ml: 2, mt: 1 },
  inputSx = {},
  containerSx,
  PaperComponent
}: RhfDetailAutocompleteFormFieldProps<
  K,
  T,
  Multiple,
  DisableClearable,
  FreeSolo
>) {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? internalInputRef;
  const [highlight, setHighlight] = useState<K | null>(null);

  useEffect(() => {
    autoFocus && inputRef.current?.focus();
  }, [autoFocus, inputRef]);

  const {
    field: { onChange, value: fieldValue, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      required: {
        value: required,
        message: `${label || "Value"} is required`
      },
      ...rules
    }
  });

  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  return (
    <Autocomplete<K, Multiple, DisableClearable, FreeSolo>
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      openOnFocus={openOnFocus}
      autoHighlight={autoHighlight}
      autoSelect={freeSolo}
      blurOnSelect={blurOnSelect}
      disablePortal={!enablePortal}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={disabled}
      options={options}
      loading={isLoading}
      noOptionsText={noOptionsText}
      fullWidth
      ListboxComponent={ListboxComponent}
      PaperComponent={PaperComponent}
      popupIcon={popupIcon}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderTags={renderTags}
      renderOption={renderOption}
      onHighlightChange={(_, newHighlight) => {
        setHighlight(newHighlight);
      }}
      renderInput={({ InputProps, ...restParams }) => (
        <TextField
          sx={sx}
          {...restParams}
          InputLabelProps={{
            ...restParams.InputLabelProps,
            disabled: false
          }}
          InputProps={{
            placeholder: placeholder,
            ...(disabled
              ? { ...InputProps, disableUnderline: disabled }
              : InputProps),
            onKeyDown: (e) => {
              if (!freeSolo && !highlight && e.key === "Enter") {
                e.stopPropagation();
              }
              if (e.key === "Enter") {
                onSubmit?.();
              }
            },
            sx: inputSx
          }}
          required={required}
          error={Boolean(errorMessage)}
          helperText={errorMessage || ""}
          disabled={disabled}
          label={label}
          onChange={(event) => {
            if (onTextInputChange) {
              onTextInputChange(event.target.value);
            }
          }}
          inputRef={inputRef}
        />
      )}
      groupBy={groupBy}
      onChange={(_, data) => {
        const transformed = transformValue ? transformValue(data) : data;
        onChange(transformed);
        if (onOptionSelected) {
          onOptionSelected(transformed, name);
        }
      }}
      sx={containerSx}
      {...restField}
      value={multiple && fieldValue == null ? EMPTY_ARRAY : fieldValue}
      {...(filterOptions ? { filterOptions } : { filterOptions: (x) => x })}
    />
  );
}
